/**
 * Always-on annotation dots.
 *
 * WHY AN OVERLAY, AND NOT MARKUP INSIDE THE APP'S ELEMENTS
 * The Timesact embed rebuilds its badge, button, message, timer and payment radio on every
 * variant change. Anything injected inside one of them is wiped on the next switch, and
 * wrapping them risks breaking the selectors the app uses to find its own nodes. So nothing
 * here touches the app's DOM: dots live in our own layer, positioned from measured rects,
 * and are re-measured whenever the page mutates or resizes.
 *
 * There is no toggle. The previous version hid everything behind a button, and a button that
 * does nothing when the app has not finished injecting is indistinguishable from a broken
 * one. Dots appear as their targets appear.
 */
(() => {
  if (window.__tsDots) return;
  window.__tsDots = true;
  const NOTES = [
    { sel: '[class*="timesact-badge"]', title: "Badge",
      body: "Set in the app: text, shape, corner and colour. It rides on the product image and on collection cards." },
    { sel: '[class*="timesact-"][class*="-button"]', title: "Button",
      body: "Timesact relabels your theme's own button — same styling, new words — or adds its own for notify flows." },
    { sel: '[class*="timesact-"][class*="-message"]', title: "Message",
      body: "The only field that resolves variables: shipping dates, stock left, pre-orders remaining, days to go." },
    { sel: ".timesact-timer", title: "Countdown",
      body: "Counts to a date, to the shipping date, or runs a rolling window that restarts for every visitor." },
    { sel: ".timesact-selling-plan-widget", title: "Payment terms",
      body: "A real Shopify purchase option. Deposits and balances are charged by Shopify on the schedule you set." },
  ];

  const scope = document.querySelector(".xp-buybox")
    || document.querySelector(".product-information")
    || document.querySelector("product-information");
  if (!scope) return;
  const host = scope.closest(".xp-grid") || scope;
  if (getComputedStyle(host).position === "static") host.style.position = "relative";
  // Its own stacking context. z-index 30 has to lift the dots over the app's badge and button,
  // but on the root context it also lifted them over the sticky header and its mega-menu (8).
  host.style.isolation = "isolate";

  const layer = document.createElement("div");
  layer.className = "ts-dots";
  host.appendChild(layer);

  const draw = () => {
    layer.textContent = "";
    const h = host.getBoundingClientRect();
    // Number them the way the eye reads them, not the order they appear in the DOM: the
    // badge sits in the media column and the rest in the buy column.
    const found = [];
    NOTES.forEach((note) => {
      // First *visible* match, not just the first: the mega-menu carries hidden copies of the
      // badge/button classes (0×0), and querySelector would stop on one and drop the note —
      // which is exactly what hid the Badge dot once the nav badges were display:none'd.
      let r = null;
      for (const el of document.querySelectorAll(note.sel)) {
        const rr = el.getBoundingClientRect();
        if (rr.width && rr.height) { r = rr; break; }
      }
      if (r) found.push({ note, r });
    });
    found.sort((a, b) => a.r.top - b.r.top);

    found.forEach(({ note, r }, i) => {
      const dot = document.createElement("span");
      dot.className = "ts-dot";
      dot.tabIndex = 0;
      // Just outside the element's top-right corner: never over the thing it describes.
      dot.style.top = `${r.top - h.top - 9}px`;
      // Right edge on desktop, left edge on a phone. At 390px the right edge of a full-width
      // button *is* the edge of the screen, so a dot pinned there always hangs off it — no
      // amount of clamping helps, because there is nowhere legal to put it.
      const narrow = document.documentElement.clientWidth < 560;
      const x = narrow ? r.left - h.left - 9 : r.right - h.left - 9;
      const maxLeft = document.documentElement.clientWidth - (h.left + 28);
      dot.style.left = `${Math.max(2, Math.min(x, maxLeft))}px`;
      dot.innerHTML = `<i aria-hidden="true">${i + 1}</i>
        <span class="ts-dot-card" role="tooltip"><b>${note.title}</b>${note.body}</span>`;
      layer.appendChild(dot);
    });
  };

  const debounce = (fn, ms) => { let t; return () => { clearTimeout(t); t = setTimeout(fn, ms); }; };
  const redraw = debounce(() => {
    // Never redraw while someone is reading a tooltip: showing the card is itself a DOM
    // change, and rebuilding the dots underneath the cursor made it flicker forever.
    if (layer.matches(":hover") || layer.contains(document.activeElement)) return;
    draw();
  }, 250);
  new MutationObserver((records) => {
    // Ignore our own layer, or we observe the thing we just drew and loop.
    if (records.every((r) => layer.contains(r.target))) return;
    redraw();
  }).observe(document.body, { childList: true, subtree: true });
  new ResizeObserver(redraw).observe(host);
  addEventListener("resize", redraw);
  addEventListener("load", redraw);
  draw();
})();
